import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { GameNotif } from '../../hooks/useGameNotifications'
import Avatar from '../common/Avatar'

interface Props {
  notification: GameNotif
  onDismiss: (id: number) => void
}

// Emoji rain for flunker notification
function LaughingEmojis() {
  const emojis = ['😂', '🤣', '😆', '💀', '😈']
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {Array.from({ length: 12 }, (_, i) => (
        <div
          key={i}
          className="absolute text-3xl select-none"
          style={{
            left: `${5 + Math.random() * 90}%`,
            bottom: `-${30 + Math.random() * 20}px`,
            animation: `emojiFloat ${2 + Math.random() * 2}s ${Math.random() * 0.8}s ease-out forwards`,
          }}
        >
          {emojis[i % emojis.length]}
        </div>
      ))}
    </div>
  )
}

// Sparkle effect for honors notification
function HonorSparkles() {
  const sparkles = ['✨', '⭐', '🌟', '💫', '🏆']
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {Array.from({ length: 10 }, (_, i) => (
        <div
          key={i}
          className="absolute text-2xl select-none"
          style={{
            left: `${5 + Math.random() * 90}%`,
            bottom: `-${20 + Math.random() * 20}px`,
            animation: `emojiFloat ${2.5 + Math.random() * 2}s ${Math.random() * 1}s ease-out forwards`,
          }}
        >
          {sparkles[i % sparkles.length]}
        </div>
      ))}
    </div>
  )
}

function ConfettiMini() {
  const COLORS = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#FF99C8', '#C9B1FF']
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {Array.from({ length: 30 }, (_, i) => (
        <div
          key={i}
          className="absolute top-0"
          style={{
            left: `${Math.random() * 100}%`,
            width: `${4 + Math.random() * 4}px`,
            height: `${6 + Math.random() * 8}px`,
            backgroundColor: COLORS[Math.floor(Math.random() * COLORS.length)],
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            animation: `confettiFall ${2 + Math.random() * 2}s ${Math.random() * 1.5}s linear forwards`,
          }}
        />
      ))}
    </div>
  )
}

export default function GameNotification({ notification, onDismiss }: Props) {
  const { t } = useTranslation()
  const [phase, setPhase] = useState<'in' | 'out'>('in')

  const { type, playerName, playerAvatar, achievementName, achievementIcon } = notification

  useEffect(() => {
    // Start fade-out 2.8s in (longer for achievements)
    const delay = type === 'achievement_unlocked' ? 4500 : 2800
    const timer = setTimeout(() => setPhase('out'), delay)
    return () => clearTimeout(timer)
  }, [type])

  const handleClick = () => {
    setPhase('out')
    setTimeout(() => onDismiss(notification.id), 300)
  }

  // Config per type
  const config: Record<string, {
    emoji: string
    emojiAnim: string
    textClass: string
    bgClass: string
    borderClass: string
    overlay: React.ReactNode
  }> = {
    school_flunker: {
      emoji: '😂',
      emojiAnim: 'laughBounce 0.6s ease-in-out infinite',
      textClass: 'text-red-300',
      bgClass: 'bg-red-900/80',
      borderClass: 'border-red-500/60',
      overlay: <LaughingEmojis />,
    },
    school_graduated: {
      emoji: '🎓',
      emojiAnim: 'trophyBounce 0.6s cubic-bezier(.36,.07,.19,.97) both',
      textClass: 'text-blue-200',
      bgClass: 'bg-blue-900/80',
      borderClass: 'border-blue-500/60',
      overlay: <ConfettiMini />,
    },
    school_honors: {
      emoji: '🎖️',
      emojiAnim: 'trophyBounce 0.6s cubic-bezier(.36,.07,.19,.97) both',
      textClass: 'text-yellow-200',
      bgClass: 'bg-yellow-900/80',
      borderClass: 'border-yellow-500/60',
      overlay: <HonorSparkles />,
    },
    small_but_mine: {
      emoji: '😎',
      emojiAnim: 'laughBounce 0.5s ease-in-out infinite',
      textClass: 'text-purple-200',
      bgClass: 'bg-purple-900/80',
      borderClass: 'border-purple-500/60',
      overlay: null,
    },
    main_game_started: {
      emoji: '🎲',
      emojiAnim: 'trophyBounce 0.6s cubic-bezier(.36,.07,.19,.97) both',
      textClass: 'text-emerald-200',
      bgClass: 'bg-emerald-900/80',
      borderClass: 'border-emerald-500/60',
      overlay: <ConfettiMini />,
    },
    poker_rolled: {
      emoji: '🎰',
      emojiAnim: 'trophyBounce 0.6s cubic-bezier(.36,.07,.19,.97) both',
      textClass: 'text-yellow-200',
      bgClass: 'bg-amber-900/85',
      borderClass: 'border-yellow-400/70 shadow-[0_0_40px_rgba(250,204,21,0.35)]',
      overlay: <HonorSparkles />,
    },
    take_the_lead: {
      emoji: '🚀',
      emojiAnim: 'trophyBounce 0.6s cubic-bezier(.36,.07,.19,.97) both',
      textClass: 'text-sky-200',
      bgClass: 'bg-sky-900/80',
      borderClass: 'border-sky-500/60',
      overlay: null,
    },
    last_round: {
      emoji: '🏁',
      emojiAnim: 'trophyBounce 0.6s cubic-bezier(.36,.07,.19,.97) both',
      textClass: 'text-orange-200',
      bgClass: 'bg-orange-900/80',
      borderClass: 'border-orange-500/60',
      overlay: null,
    },
    crossed_out: {
      emoji: '😬',
      emojiAnim: 'laughBounce 0.5s ease-in-out infinite',
      textClass: 'text-slate-300',
      bgClass: 'bg-slate-800/85',
      borderClass: 'border-slate-500/50',
      overlay: null,
    },
    achievement_unlocked: {
      emoji: achievementIcon || '🏆',
      emojiAnim: 'trophyBounce 0.6s cubic-bezier(.36,.07,.19,.97) both',
      textClass: 'text-green-300',
      bgClass: 'bg-green-900/90',
      borderClass: 'border-green-400/50 shadow-[0_0_30px_rgba(34,197,94,0.3)]',
      overlay: <HonorSparkles />,
    },
  }

  const c = config[type] ?? config.school_graduated

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-auto"
      onClick={handleClick}
    >
      {/* Overlay effects */}
      {c.overlay}

      {/* Main notification card */}
      <div
        className="relative px-8 py-6 rounded-3xl text-center max-w-xs"
        style={{
          animation: phase === 'in'
            ? 'notifSlideUp 0.5s ease-out both'
            : 'notifFadeOut 0.3s ease-in both',
        }}
      >
        <div className={`rounded-3xl border-2 ${c.bgClass} ${c.borderClass} px-8 py-6 backdrop-blur-md`}>
          {type === 'achievement_unlocked' && (
            <div className="text-[10px] font-black text-green-400 uppercase tracking-widest mb-1">
              {t('notifications.achievementUnlocked')}
            </div>
          )}
          {/* Emoji */}
          <div
            className="text-6xl mb-3 select-none"
            style={{ animation: c.emojiAnim }}
          >
            {c.emoji}
          </div>

          {/* Player avatar + name (game-wide notifications have no player) */}
          {playerName && (
            <div className="flex items-center justify-center gap-2 mb-2">
              <Avatar src={playerAvatar} size="sm" />
              <span className="text-sm font-semibold text-white/80">{playerName}</span>
            </div>
          )}

          {/* Title text */}
          <div
            className={`text-2xl font-black ${c.textClass} tracking-wide`}
            style={
              type === 'school_honors' || type === 'achievement_unlocked'
                ? {
                    backgroundImage: type === 'achievement_unlocked' 
                        ? 'linear-gradient(90deg, #4ade80, #22c55e, #4ade80)'
                        : 'linear-gradient(90deg, #FFD700, #FFA500, #FFD700, #FFA500)',
                    backgroundSize: '200% auto',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    animation: 'shineSweep 2s linear infinite',
                  }
                : undefined
            }
          >
            {type === 'achievement_unlocked' ? achievementName : t(`notifications.${type}`)}
          </div>
        </div>
      </div>
    </div>
  )
}
